import { MissingElementWarningsSkeleton } from "@/components/skeletons/MissingElementWarningSkeleton";
import { useAppSelector } from "@/store/hooks";
import { getSlideWarning } from "@ai-teach/utils";
import { HiCheckCircle, HiExclamationCircle } from "react-icons/hi";
import { MdPhoneAndroid } from "react-icons/md";
import { getGrammarFormPublicationState } from "../publication/grammarFormPublicationState";

export function MissingElementWarnings() {
  // Get current slide state from Redux
  const { slide } = useAppSelector((state) => state.slideEditor.present);

  const isGrammarFormSlide = slide?.template === "conversation";
  const publicationState = slide ? getGrammarFormPublicationState(slide) : "not_applicable";
  const isBlockedDraft = publicationState === "blocked_draft";

  const slideWarnings = getSlideWarning(slide);

  // Blocking statuses: a missing required asset ("incomplete") OR invalid content ("error", round 8).
  const isBlocking = (status: string) => status === "incomplete" || status === "error";

  // Blocking issues surface FIRST for grammar-form slides, so the author fixes publish-blockers before
  // scrolling past advisory/complete rows.
  const orderedSlideWarnings = isGrammarFormSlide
    ? slideWarnings.map((block) => ({
        ...block,
        warningItem: [...block.warningItem].sort((a, b) => {
          const aBlocking = isBlocking(a.status) ? 0 : 1;
          const bBlocking = isBlocking(b.status) ? 0 : 1;
          return aBlocking - bBlocking;
        }),
      }))
    : slideWarnings;

  // Calculate if all blocks are complete (no blocking items)
  const hasIncomplete = slideWarnings.some((block) =>
    block.warningItem.some((item) => isBlocking(item.status))
  );
  const allComplete = !hasIncomplete;

  // Show placeholder instead of unmounting to prevent CLS
  if (!slide) {
    return <MissingElementWarningsSkeleton />;
  }

  // Count total blocking items across all blocks
  const totalIncomplete = slideWarnings.reduce(
    (sum, block) => sum + block.warningItem.filter((item) => isBlocking(item.status)).length,
    0
  );

  return (
    <div className="w-72 border-l border-slate-200 flex flex-col flex-1 min-h-0 overflow-hidden">
      {/* Header */}
      <div
        className={`flex items-center gap-2 px-4 py-2 border-b flex-shrink-0 ${
          allComplete
            ? "bg-green-50 border-green-200"
            : "bg-gradient-to-br from-yellow-50 to-orange-50 border-yellow-200"
        }`}
      >
        {allComplete ? (
          <HiCheckCircle className="w-6 h-6 text-green-600 flex-shrink-0" />
        ) : (
          <HiExclamationCircle className="w-6 h-6 text-yellow-600 flex-shrink-0" />
        )}
        <div className="min-w-0 overflow-hidden">
          <h4 className="font-semibold text-secondary text-sm truncate">
            {isGrammarFormSlide ? "Publication checklist" : "Asset Checklist"}
          </h4>
          <p className="text-xs text-gray-600 truncate">
            {allComplete
              ? "All required elements added"
              : `${totalIncomplete} issue${totalIncomplete !== 1 ? "s" : ""} to resolve`}
          </p>
        </div>
      </div>

      {/* Draft notice - grammar-form slides only, shown whenever the slide isn't published */}
      {isGrammarFormSlide && !slide?.isCompleted && (
        <div className="mx-4 mt-2 py-1 px-3 bg-amber-50 border-l-4 border-amber-500 rounded flex-shrink-0">
          <p className="text-xs text-amber-800">Draft slides are not shown to students.</p>
          {isBlockedDraft && (
            <p className="text-xs text-error-800 mt-1">
              This slide is not published. Fix the highlighted errors, then publish it.
            </p>
          )}
        </div>
      )}

      {/* Mobile Warning - Only show when incomplete */}
      {!allComplete && (
        <div className="mx-4 mt-2 py-1 px-3 bg-error-50 border-l-4 border-error-500 rounded flex-shrink-0">
          <div className="flex items-start gap-2">
            <MdPhoneAndroid className="w-5 h-5 text-error-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-error-800">Mobile Warning</p>
              <p className="text-[10px] text-error-700">
                Incomplete slides will not be shown on the mobile app
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Asset Checklist - SCROLLABLE */}
      <div className="overflow-y-auto flex-1 min-h-0 scrollbar-thin scrollbar-thumb-slate-300 scrollbar-track-transparent hover:scrollbar-thumb-slate-400">
        <div className="px-4 py-3 space-y-1">
          {orderedSlideWarnings.length === 0 ? (
            <div className="text-sm text-gray-600 text-center py-8 px-4">
              <HiCheckCircle className="w-12 h-12 text-green-500 mx-auto mb-3" />
              <p className="font-medium text-green-800">All set!</p>
              <p className="text-xs text-gray-500 mt-1">Your slide is ready for mobile</p>
            </div>
          ) : (
            orderedSlideWarnings.map((warningBlock, blockIndex) => {
              if (warningBlock.warningItem.length === 0) return null;

              const hasIncompleteInBlock = warningBlock.warningItem.some((item) =>
                isBlocking(item.status)
              );

              return (
                <div
                  key={`warning-block-${blockIndex + 1}`}
                  className={`border rounded-lg p-3 space-y-2 ${
                    hasIncompleteInBlock
                      ? "bg-error-50 border-error-200"
                      : "bg-green-50/50 border-green-200"
                  }`}
                >
                  {/* Block Header */}
                  <div
                    className={`flex items-center gap-2 pb-2 border-b ${
                      hasIncompleteInBlock ? "border-error-200/50" : "border-green-200/50"
                    }`}
                  >
                    <span
                      className={`text-xs flex gap-1 font-semibold ${
                        hasIncompleteInBlock ? "text-error-800" : "text-success"
                      }`}
                    >
                      <span className="rounded-full w-4 aspect-square text-center">
                        {blockIndex + 1}.
                      </span>
                      {warningBlock.title}
                    </span>
                  </div>

                  {/* Asset Items */}
                  {warningBlock.warningItem.map((item, itemIndex) => (
                    <div
                      key={`item-${blockIndex}-${itemIndex + 1}`}
                      className="flex items-center gap-2 text-sm"
                    >
                      {item.status === "complete" ? (
                        <HiCheckCircle className="w-4 h-4 text-success flex-shrink-0" />
                      ) : (
                        <HiExclamationCircle
                          className={`w-4 h-4 flex-shrink-0 ${
                            item.status === "warning" ? "text-amber-500" : "text-error-600"
                          }`}
                        />
                      )}
                      <span
                        className={`flex-1 ${
                          item.status === "complete"
                            ? "text-success"
                            : item.status === "warning"
                              ? "text-amber-700"
                              : "text-error-800"
                        }`}
                      >
                        {item.asset}
                        {item.current !== undefined && item.required !== undefined && (
                          <span className="ml-1 text-xs font-semibold">
                            ({item.current}/{item.required})
                          </span>
                        )}
                      </span>
                    </div>
                  ))}
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}
